import _typeof from "@babel/runtime/helpers/typeof";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';
import { Map, List } from 'immutable';
import { useContextMenu } from '../contexts/ContextMenuContext';
import { normalizeForSearch } from '../utils/searchUtils';
import { copyToClipboard } from '../utils/copyToClipboard';
import StateNode from './StateNode';
import PrimitiveValue from './PrimitiveValue';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Component for recursively rendering Immutable.js objects
 * @param props - Component props
 * @returns Rendered component
 */
var ImmutableRenderer = /*#__PURE__*/memo(function (_ref) {
  var data = _ref.data,
    _ref$searchPath = _ref.searchPath,
    searchPath = _ref$searchPath === void 0 ? '' : _ref$searchPath,
    _ref$currentPath = _ref.currentPath,
    currentPath = _ref$currentPath === void 0 ? '' : _ref$currentPath,
    _ref$expandedPaths = _ref.expandedPaths,
    expandedPaths = _ref$expandedPaths === void 0 ? {} : _ref$expandedPaths,
    _ref$matchingPaths = _ref.matchingPaths,
    matchingPaths = _ref$matchingPaths === void 0 ? [] : _ref$matchingPaths,
    onHoverPath = _ref.onHoverPath;
  var _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    internalExpandedKeys = _useState2[0],
    setInternalExpandedKeys = _useState2[1];
  var _useContextMenu = useContextMenu(),
    openContextMenu = _useContextMenu.openContextMenu;

  // Combine external path expansion (from search) with internal expand/collapse state
  var expandedKeys = useMemo(function () {
    return _objectSpread(_objectSpread({}, internalExpandedKeys), expandedPaths);
  }, [internalExpandedKeys, expandedPaths]);
  var toggleExpand = useCallback(function (key) {
    var path = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    // For array items and list items, we need to use the full path as the key
    var toggleKey = path ? "".concat(path, ".").concat(key) : key;
    var uniqueToggleKey = !isNaN(Number(key)) ? "array-item-".concat(toggleKey) // Use array-item prefix for numeric keys (array indices)
    : toggleKey;
    setInternalExpandedKeys(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, uniqueToggleKey, !prev[uniqueToggleKey]));
    });
  }, []);

  // Fixed collapse function - ensures parent node remains collapsible afterward
  var collapseChildren = useCallback(function (path) {
    if (!path) return;
    console.log("Collapsing ALL descendants of node: \"".concat(path, "\""));
    setInternalExpandedKeys(function (prev) {
      // Start with a fresh state object
      var newState = {};

      // Step 1: Instead of forcing the parent node to stay expanded,
      // just preserve its current expansion state (if any)
      if (prev[path] !== undefined) {
        newState[path] = prev[path];
      }

      // Preserve alternate formats of the parent path with their original values
      // This allows them to be properly toggled later
      var parentFormats = ["array-item-".concat(path), "list-item-".concat(path), "path-".concat(path)];
      parentFormats.forEach(function (format) {
        if (prev[format] !== undefined) {
          newState[format] = prev[format];
        }
      });

      // Step 2: Copy keys unrelated to our path
      Object.keys(prev).forEach(function (key) {
        // Skip keys we've already processed
        if (newState[key] !== undefined) return;
        var keyLower = key.toLowerCase();
        var pathLower = path.toLowerCase();

        // Skip children of our path
        if (keyLower === pathLower || keyLower.startsWith("".concat(pathLower, "."))) return;

        // Skip prefixed versions of our path's children
        for (var _i = 0, _arr = ['array-item-', 'list-item-', 'path-']; _i < _arr.length; _i++) {
          var prefix = _arr[_i];
          if (keyLower.startsWith("".concat(prefix).concat(pathLower))) return;
        }

        // Skip exact-array keys containing our path
        if (key.startsWith('exact-array-') && key.includes(path)) return;

        // Special handling for peer nodes
        if (path.includes('.')) {
          var lastDot = path.lastIndexOf('.');
          var parentPath = path.substring(0, lastDot);
          var childKey = path.substring(lastDot + 1);

          // Skip paths that are peer nodes of our target
          if (keyLower.startsWith(parentPath.toLowerCase()) && key !== parentPath && !keyLower.startsWith("".concat(parentPath.toLowerCase(), ".").concat(childKey.toLowerCase()))) {
            return;
          }
        }

        // Keep all other keys
        newState[key] = prev[key];
      });
      return newState;
    });
  }, []);

  // Add debug info for expanded keys when needed
  useEffect(function () {
    console.debug("Current expanded keys count: ".concat(Object.keys(internalExpandedKeys).length));
  }, [internalExpandedKeys]);
  var handleContextMenu = useCallback(function (e, item, path) {
    e.preventDefault();
    e.stopPropagation();

    // Extract the last segment from the path for the key
    var segments = path.split('.');
    var key = segments[segments.length - 1];
    var menuItems = [{
      label: 'Copy Value',
      action: function action() {
        return copyToClipboard(item);
      }
    }, {
      label: 'Copy with Index',
      action: function action() {
        return copyToClipboard(item, key);
      }
    }, {
      label: 'Copy Full Path',
      action: function action() {
        return copyToClipboard(item, null, path);
      }
    }];

    // Add collapse children option if the item is an object or array
    if (_typeof(item) === 'object' && item !== null && (Object.keys(item).length > 0 || Array.isArray(item) && item.length > 0 || Map.isMap(item) && item.size > 0 || List.isList(item) && item.size > 0)) {
      menuItems.push({
        label: 'Collapse Children',
        action: function action() {
          return collapseChildren(path);
        }
      });
    }
    openContextMenu({
      x: e.clientX,
      y: e.clientY,
      menuItems: menuItems
    });
  }, [openContextMenu, collapseChildren]);

  // New handler for mouse enter on any node
  var handleNodeMouseEnter = useCallback(function (path) {
    // Pass the full path up to parent components
    if (onHoverPath) onHoverPath(path);
  }, [onHoverPath]);

  // New handler for mouse leave - use empty string to avoid null issues
  var handleNodeMouseLeave = useCallback(function () {
    // Clear path when mouse leaves - explicitly set to empty string
    if (onHoverPath) onHoverPath('');
  }, [onHoverPath]);
  var renderValue = useCallback(function (value) {
    var nestLevel = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    var path = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : currentPath;
    // Limit rendering depth to prevent deep recursion
    if (nestLevel > 8) {
      return /*#__PURE__*/_jsx("span", {
        className: "text-gray-500",
        children: "Max depth reached..."
      });
    }

    // Handle immutable maps
    if (Map.isMap(value)) {
      var entries = value.entrySeq().sortBy(function (_ref2) {
        var _ref3 = _slicedToArray(_ref2, 1),
          k = _ref3[0];
        return k;
      }).toArray();
      return /*#__PURE__*/_jsx("div", {
        style: {
          marginLeft: "".concat(nestLevel * 10, "px")
        },
        children: entries.map(function (_ref4) {
          var _ref5 = _slicedToArray(_ref4, 2),
            k = _ref5[0],
            v = _ref5[1];
          var keyPath = path ? "".concat(path, ".").concat(k) : k;
          // Check if this path is on the way to a matching value
          var isOnPathToMatch = matchingPaths.some(function (matchPath) {
            return matchPath.startsWith(keyPath + '.') || path && matchPath.startsWith(path + '.') && matchPath.includes(".".concat(k, "."));
          });

          // Determine if this item is expanded
          var isExpanded = expandedKeys[k] || expandedKeys[keyPath] || searchPath && normalizeForSearch(keyPath) === normalizeForSearch(searchPath) || isOnPathToMatch;
          return /*#__PURE__*/_jsx(StateNode, {
            nodeKey: k,
            nodeValue: v,
            path: path,
            searchPath: searchPath,
            matchingPaths: matchingPaths,
            nestLevel: nestLevel,
            isExpanded: isExpanded,
            onToggle: toggleExpand,
            renderValue: renderValue,
            onCollapseChildren: collapseChildren,
            onMouseEnter: function onMouseEnter() {
              return handleNodeMouseEnter(keyPath);
            },
            onMouseLeave: handleNodeMouseLeave
          }, k);
        })
      });
    }

    // Handle immutable lists
    if (List.isList(value)) {
      var list = value.toArray();
      return /*#__PURE__*/_jsx("div", {
        style: {
          marginLeft: "".concat(nestLevel * 10, "px")
        },
        children: list.map(function (item, index) {
          var indexPath = path ? "".concat(path, ".").concat(index) : "".concat(index);

          // Check if this array item's path is on the way to a matching value
          var isOnPathToMatch = matchingPaths.some(function (matchPath) {
            return matchPath.startsWith(indexPath + '.') || path && matchPath.startsWith(path + '.') && matchPath.includes(".".concat(index, "."));
          });

          // Use full path as toggle key including prefix to avoid collisions
          var itemToggleKey = "list-item-".concat(indexPath);
          var exactItemKey = "exact-array-".concat(path, "-").concat(index);

          // Check multiple key formats for expansion state
          var isItemExpanded = expandedKeys[itemToggleKey] || expandedKeys[exactItemKey] || expandedKeys["path-".concat(indexPath)] || expandedKeys["array-item-".concat(indexPath)] || isOnPathToMatch;

          // Check if item is an object or array that can be expanded
          var isExpandableItem = _typeof(item) === 'object' && item !== null && (Object.keys(item).length > 0 || Array.isArray(item) && item.length > 0 || Map.isMap(item) && item.size > 0 || List.isList(item) && item.size > 0);

          // For list items, render our own UI instead of using StateNode
          return /*#__PURE__*/_jsxs("div", {
            className: "my-1",
            onContextMenu: function onContextMenu(e) {
              return handleContextMenu(e, item, indexPath);
            },
            onMouseEnter: function onMouseEnter() {
              return handleNodeMouseEnter(indexPath);
            },
            onMouseLeave: handleNodeMouseLeave,
            children: [isExpandableItem ? /*#__PURE__*/_jsxs("div", {
              className: "flex items-start justify-between",
              children: [/*#__PURE__*/_jsxs("div", {
                onClick: function onClick() {
                  return toggleExpand(String(index), path);
                },
                className: "ml-4 flex cursor-pointer font-semibold hover:text-gray-600",
                children: [/*#__PURE__*/_jsx("div", {
                  className: "w-6",
                  children: isItemExpanded ? '📂' : '📁'
                }), "[", index, "]:"]
              }), /*#__PURE__*/_jsx("div", {
                className: "flex items-center",
                children: !isItemExpanded && /*#__PURE__*/_jsx("div", {
                  className: "ml-2 text-gray-500",
                  children: Array.isArray(item) ? "Array(".concat(item.length, ")") : Map.isMap(item) ? "Map(".concat(item.size, ")") : List.isList(item) ? "List(".concat(item.size, ")") : "Object(".concat(Object.keys(item).length, ")")
                })
              })]
            }) : /*#__PURE__*/_jsx("div", {
              className: "flex items-center justify-between",
              children: /*#__PURE__*/_jsxs("div", {
                className: "flex items-center",
                children: [/*#__PURE__*/_jsxs("span", {
                  className: "mr-2 ml-10 font-semibold",
                  children: ["[", index, "]:"]
                }), /*#__PURE__*/_jsx(PrimitiveValue, {
                  value: item,
                  searchPath: searchPath
                })]
              })
            }), isExpandableItem && isItemExpanded && /*#__PURE__*/_jsx("div", {
              className: "pl-4",
              children: renderValue(item, nestLevel + 1, indexPath)
            })]
          }, index);
        })
      });
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return /*#__PURE__*/_jsx("div", {
        style: {
          marginLeft: "".concat(nestLevel * 10, "px")
        },
        children: value.map(function (item, index) {
          var indexPath = path ? "".concat(path, ".").concat(index) : "".concat(index);

          // Check if this array item's path is on the way to a matching value
          var isOnPathToMatch = matchingPaths.some(function (matchPath) {
            return matchPath.startsWith(indexPath + '.') || path && matchPath.startsWith(path + '.') && matchPath.includes(".".concat(index, "."));
          });

          // Use full path as toggle key including prefix to avoid collisions
          var itemToggleKey = "array-item-".concat(indexPath);
          var exactItemKey = "exact-array-".concat(path, "-").concat(index);

          // Check multiple key formats for expansion state
          var isItemExpanded = expandedKeys[itemToggleKey] || expandedKeys[exactItemKey] || expandedKeys["path-".concat(indexPath)] || isOnPathToMatch;

          // Check if item is an object or array that can be expanded
          var isExpandableItem = _typeof(item) === 'object' && item !== null && (Object.keys(item).length > 0 || Array.isArray(item) && item.length > 0 || Map.isMap(item) && item.size > 0 || List.isList(item) && item.size > 0);

          // For array items, render our own UI instead of using StateNode
          return /*#__PURE__*/_jsxs("div", {
            className: "my-1",
            onContextMenu: function onContextMenu(e) {
              return handleContextMenu(e, item, indexPath);
            },
            onMouseEnter: function onMouseEnter() {
              return handleNodeMouseEnter(indexPath);
            },
            onMouseLeave: handleNodeMouseLeave,
            children: [isExpandableItem ? /*#__PURE__*/_jsxs("div", {
              className: "flex items-start justify-between",
              children: [/*#__PURE__*/_jsxs("div", {
                onClick: function onClick() {
                  return toggleExpand(String(index), path);
                },
                className: "ml-4 flex cursor-pointer font-semibold hover:text-gray-600",
                children: [/*#__PURE__*/_jsx("div", {
                  className: "w-6",
                  children: isItemExpanded ? '📂' : '📁'
                }), "[", index, "]:"]
              }), /*#__PURE__*/_jsx("div", {
                className: "flex items-center",
                children: !isItemExpanded && /*#__PURE__*/_jsx("div", {
                  className: "ml-2 text-gray-500",
                  children: Array.isArray(item) ? "Array(".concat(item.length, ")") : Map.isMap(item) ? "Map(".concat(item.size, ")") : List.isList(item) ? "List(".concat(item.size, ")") : "Object(".concat(Object.keys(item).length, ")")
                })
              })]
            }) : /*#__PURE__*/_jsx("div", {
              className: "flex items-center justify-between",
              children: /*#__PURE__*/_jsxs("div", {
                className: "flex items-center",
                children: [/*#__PURE__*/_jsxs("span", {
                  className: "mr-2 ml-10 font-semibold",
                  children: ["[", index, "]:"]
                }), /*#__PURE__*/_jsx(PrimitiveValue, {
                  value: item,
                  searchPath: searchPath
                })]
              })
            }), isExpandableItem && isItemExpanded && /*#__PURE__*/_jsx("div", {
              className: "pl-4",
              children: renderValue(item, nestLevel + 1, indexPath)
            })]
          }, index);
        })
      });
    }

    // Primitive values - use memoized component
    return /*#__PURE__*/_jsx(PrimitiveValue, {
      value: value,
      searchPath: searchPath
    });
  }, [currentPath, expandedKeys, matchingPaths, searchPath, toggleExpand, collapseChildren, handleContextMenu, handleNodeMouseEnter, handleNodeMouseLeave]);
  return renderValue(data);
});
export default ImmutableRenderer;